/**
 * Validace grafu automatizace — sdílená klient (editor live) i server (uložení).
 * Vrstvy: (1) strukturální zod, (2) limity, (3) známé typy + params zod, (4) hrany
 * (existence, typecheck, kardinalita), (5) DAG (Kahn), (6) ≥1 trigger.
 */

import { automationGraphSchema } from "@globals/automations/graphSchema";
import { DATA_TYPE_LABEL, handleLabel, NODE_LABEL, paramLabel } from "@globals/automations/labels";
import { getNodeEntry, NODE_PARAM_SCHEMAS, resolveNodeHandles, typesCompatible } from "@globals/automations/registry";
import type {
  AutomationGraph,
  AutomationNode,
  GraphValidationIssue,
  GraphValidationResult,
} from "@globals/automations/types";

const MAX_NODES = 100;
const MAX_EDGES = 300;

const maxEdgesForHandle = (dataType: string, override?: number): number =>
  override ?? (dataType === "pulse" ? Number.POSITIVE_INFINITY : 1);

/** Uzly, kde „aspoň 2 zapojené vstupy" dává smysl (variadická logika). */
const VARIADIC_TYPES = new Set(["logic.and", "logic.or"]);

/**
 * Uzly, které potřebují STAV držící se v čase — přivedená událost platí jen okamžik,
 * takže by se doba nikdy nenaplnila. Neblokuje uložení, jen varuje.
 */
const NEEDS_SUSTAINED_INPUT: Record<string, string> = {
  "time.sustained": "Trvá po dobu",
  "logic.hysteresis": "Hystereze",
};

/** Lidský název uzlu pro hlášky: uživatelský popisek → český název typu → typ. */
const nodeName = (node: AutomationNode): string => node.label?.trim() || NODE_LABEL[node.type] || node.type;

export const validateGraph = (input: unknown): GraphValidationResult => {
  const errors: GraphValidationIssue[] = [];
  const warnings: GraphValidationIssue[] = [];

  // (1) strukturální
  const parsed = automationGraphSchema.safeParse(input);
  if (!parsed.success) {
    return { ok: false, errors: [{ code: "MALFORMED", message: "Graf má neplatnou strukturu." }], warnings };
  }
  const graph = parsed.data as AutomationGraph;

  // (2) limity
  if (graph.nodes.length > MAX_NODES) {
    errors.push({ code: "GRAPH_TOO_LARGE", message: `Maximálně ${MAX_NODES} uzlů (má ${graph.nodes.length}).` });
  }
  if (graph.edges.length > MAX_EDGES) {
    errors.push({ code: "GRAPH_TOO_LARGE", message: `Maximálně ${MAX_EDGES} hran (má ${graph.edges.length}).` });
  }

  // id uniqueness
  const nodeById = new Map<string, AutomationNode>();
  for (const node of graph.nodes) {
    if (nodeById.has(node.id)) {
      errors.push({ code: "DUPLICATE_NODE_ID", message: `Duplicitní id uzlu "${node.id}".`, nodeId: node.id });
    }
    nodeById.set(node.id, node);
  }

  // (3) známé typy + params — chyba MUSÍ jmenovat konkrétní pole, ne jen id uzlu
  for (const node of graph.nodes) {
    const entry = getNodeEntry(node.type);
    if (!entry) {
      errors.push({ code: "UNKNOWN_NODE_TYPE", message: `Neznámý typ uzlu "${node.type}".`, nodeId: node.id });
      continue;
    }
    const schema = NODE_PARAM_SCHEMAS[node.type];
    const r = schema.safeParse(node.params);
    if (!r.success) {
      const seen = new Set<string>();
      for (const issue of r.error.issues) {
        const key = String(issue.path[0] ?? "");
        if (!key || seen.has(key)) continue;
        seen.add(key);
        const missing = issue.code === "invalid_type" || issue.code === "too_small";
        errors.push({
          code: "NODE_PARAMS_INVALID",
          message: missing
            ? `Uzel „${nodeName(node)}": vyplň pole „${paramLabel(node.type, key)}".`
            : `Uzel „${nodeName(node)}": pole „${paramLabel(node.type, key)}" má neplatnou hodnotu.`,
          nodeId: node.id,
        });
      }
      if (seen.size === 0) {
        errors.push({
          code: "NODE_PARAMS_INVALID",
          message: `Uzel „${nodeName(node)}" má neplatné nastavení.`,
          nodeId: node.id,
        });
      }
    }
  }

  // resolved handles cache (jen pro uzly se známým typem); hrany se předávají,
  // aby variadické uzly (A zároveň / Nebo) rostly podle skutečného zapojení
  const handlesOf = new Map<string, ReturnType<typeof resolveNodeHandles>>();
  for (const node of graph.nodes) {
    if (getNodeEntry(node.type)) handlesOf.set(node.id, resolveNodeHandles(node, graph.edges));
  }

  // (4) hrany: existence + typecheck + kardinalita
  const edgesIntoHandle = new Map<string, number>(); // `${target}|${targetHandle}` → count
  for (const edge of graph.edges) {
    const src = nodeById.get(edge.source);
    const tgt = nodeById.get(edge.target);
    if (!src || !tgt) {
      errors.push({ code: "EDGE_DANGLING", message: "Hrana odkazuje na neexistující uzel.", edgeId: edge.id });
      continue;
    }
    const srcH = handlesOf.get(edge.source)?.outputs.find((h) => h.id === edge.sourceHandle);
    const tgtH = handlesOf.get(edge.target)?.inputs.find((h) => h.id === edge.targetHandle);
    if (!srcH || !tgtH) {
      errors.push({ code: "EDGE_BAD_HANDLE", message: "Hrana odkazuje na neexistující port.", edgeId: edge.id });
      continue;
    }
    if (!typesCompatible(srcH.dataType, tgtH.dataType)) {
      errors.push({
        code: "GRAPH_TYPE_MISMATCH",
        message: `„${DATA_TYPE_LABEL[srcH.dataType]}" nejde přivést do vstupu „${DATA_TYPE_LABEL[tgtH.dataType]}" (${nodeName(src)} → ${nodeName(tgt)}).`,
        edgeId: edge.id,
      });
    }
    const key = `${edge.target}|${edge.targetHandle}`;
    const count = (edgesIntoHandle.get(key) ?? 0) + 1;
    edgesIntoHandle.set(key, count);
    if (count > maxEdgesForHandle(tgtH.dataType, tgtH.maxEdges)) {
      errors.push({
        code: "HANDLE_OCCUPIED",
        message: `Do vstupu „${handleLabel(tgtH.labelKey, tgtH.id)}" uzlu „${nodeName(tgt)}" už jeden drát vede.`,
        edgeId: edge.id,
      });
    }
    // Sémantické varování: událost trvá jen okamžik, takže „Trvá po dobu" ani
    // hystereze se z ní nikdy nenaplní. Neblokuje uložení — jen upozorní.
    if (srcH.dataType === "pulse" && NEEDS_SUSTAINED_INPUT[tgt.type] && tgtH.dataType !== "pulse") {
      warnings.push({
        code: "PULSE_INTO_SUSTAINED",
        message: `Do uzlu „${NEEDS_SUSTAINED_INPUT[tgt.type]}" vede událost — ta platí jen okamžik, takže se doba nikdy nenaplní. Přiveď sem podmínku (např. z uzlu Porovnání).`,
        edgeId: edge.id,
        nodeId: tgt.id,
      });
    }
  }

  // (4b) nezapojené povinné vstupy — nejčastější příčina „nic to nedělá"
  for (const node of graph.nodes) {
    const handles = handlesOf.get(node.id);
    if (!handles) continue;
    if (VARIADIC_TYPES.has(node.type)) {
      const connected = handles.inputs.filter((h) => (edgesIntoHandle.get(`${node.id}|${h.id}`) ?? 0) > 0).length;
      if (connected < 2) {
        errors.push({
          code: "VARIADIC_TOO_FEW",
          message: `Uzel „${nodeName(node)}" (${NODE_LABEL[node.type]}) potřebuje aspoň dva zapojené vstupy — teď má ${connected}.`,
          nodeId: node.id,
        });
      }
      continue;
    }
    for (const h of handles.inputs) {
      if (h.optional) continue;
      if ((edgesIntoHandle.get(`${node.id}|${h.id}`) ?? 0) > 0) continue;
      errors.push({
        code: "INPUT_NOT_CONNECTED",
        message: `Uzel „${nodeName(node)}": vstup „${handleLabel(h.labelKey, h.id)}" není zapojený.`,
        nodeId: node.id,
      });
    }
  }

  // (5) DAG (Kahn) — cyklus zakázán
  const indeg = new Map<string, number>();
  const adj = new Map<string, string[]>();
  for (const node of graph.nodes) indeg.set(node.id, 0);
  for (const edge of graph.edges) {
    if (!nodeById.has(edge.source) || !nodeById.has(edge.target)) continue;
    adj.set(edge.source, [...(adj.get(edge.source) ?? []), edge.target]);
    indeg.set(edge.target, (indeg.get(edge.target) ?? 0) + 1);
  }
  const queue = [...indeg.entries()].filter(([, d]) => d === 0).map(([id]) => id);
  let visited = 0;
  while (queue.length > 0) {
    const id = queue.shift() as string;
    visited++;
    for (const next of adj.get(id) ?? []) {
      const d = (indeg.get(next) ?? 0) - 1;
      indeg.set(next, d);
      if (d === 0) queue.push(next);
    }
  }
  if (visited < graph.nodes.length) {
    errors.push({ code: "GRAPH_CYCLE", message: "Graf obsahuje smyčku — spojení musí být acyklické." });
  }

  // (6) ≥1 trigger
  const hasTrigger = graph.nodes.some((n) => getNodeEntry(n.type)?.category === "trigger");
  if (graph.nodes.length > 0 && !hasTrigger) {
    warnings.push({ code: "NO_TRIGGER", message: "Chybí spouštěč — automatizace by se nikdy nespustila." });
  }

  // Notifikace bez titulku dorazí do telefonu prázdná
  for (const node of graph.nodes) {
    if (node.type !== "action.notify") continue;
    const title = typeof node.params.title === "string" ? node.params.title.trim() : "";
    if (title.length === 0) {
      warnings.push({
        code: "NOTIFY_NO_TITLE",
        message: `Uzel „${nodeName(node)}" nemá titulek — notifikace dorazí bez nadpisu.`,
        nodeId: node.id,
      });
    }
  }

  // ≥1 akce (jinak nic nedělá)
  const hasAction = graph.nodes.some((n) => getNodeEntry(n.type)?.category === "action");
  if (graph.nodes.length > 0 && !hasAction) {
    warnings.push({ code: "NO_ACTION", message: "Automatizace nemá žádnou akci." });
  }

  return { ok: errors.length === 0, errors, warnings };
};

const TOPIC_PARAM_TYPES = new Set([
  "value.topic",
  "value.lastDb",
  "value.age",
  "trigger.topicChange",
  "math.deltaOverTime",
]);

const PLACEHOLDER_RE = /\{([^}]+)\}/g;

/**
 * Topicy, na jejichž MQTT event se má graf vyhodnotit (→ Automation.triggerTopics,
 * hot-path routing v triggers.ts). Sbírá topicId params + {topic} reference z formula uzlů.
 */
export const extractTriggerTopics = (graph: AutomationGraph): string[] => {
  const topics = new Set<string>();
  for (const node of graph.nodes) {
    if (TOPIC_PARAM_TYPES.has(node.type)) {
      const t = node.params.topicId;
      if (typeof t === "string" && t.length > 0) topics.add(t);
    }
    if (node.type === "math.formula" && typeof node.params.expression === "string") {
      let m: RegExpExecArray | null;
      PLACEHOLDER_RE.lastIndex = 0;
      while ((m = PLACEHOLDER_RE.exec(node.params.expression)) !== null) {
        topics.add(m[1].trim());
      }
    }
  }
  return [...topics];
};
